import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Search, Target, TrendingUp, Filter, FileText } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexProjectSales } from "../../../services/convexService";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";

const ProjectSales = () => {
  const { user } = useAuth();
  const { getProjectsByStatus } = useConvexProjects();
  const { getProjectSalesByStatus, acceptProjectSale, getAllProjectSales } = useConvexProjectSales();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Get the data using hooks directly (these are at the component level)
  const allProjects = getProjectsByStatus("approved");
  const allProjectSales = getAllProjectSales();

  const [availableProjects, setAvailableProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(t("common.all") || "الكل");

  // Process data when hooks return values
  useEffect(() => {
    if (allProjects !== undefined && allProjectSales !== undefined) {
      // Filter projects that have sell purpose and combine with their sales info
      const sellProjects = allProjects.filter(project => project.projectPurpose === "sell");

      // Combine the data to show projects with their sales info
      const projectsWithSales = sellProjects.map(project => {
        const sale = allProjectSales.find((s: any) => s.projectId === project._id);
        return sale ? { ...project, sale } : { ...project, sale: null }; // Include projects even without active sale
      });

      setAvailableProjects(projectsWithSales);
      setLoading(false);
    } else if (allProjects === null || allProjectSales === null) {
      // If either query is loading, keep loading state
      setLoading(true);
    }
  }, [allProjects, allProjectSales]);

  // Get unique categories from projects
  const categories = [t("common.all") || "الكل", ...new Set(availableProjects.map(project => project?.category))];

  const filteredProjects = availableProjects.filter((item) => {
    if (!item) return false;
    const project = item;
    const matchesSearch = project.title.includes(searchTerm) || project.description.includes(searchTerm);
    const matchesCategory = selectedCategory === (t("common.all") || "الكل") || project.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAcceptSale = async (projectSaleId: string) => {
    if (!user) {
      toast.error("You must be logged in to accept a project sale");
      navigate("/login");
      return;
    }

    try {
      await acceptProjectSale({
        projectSaleId: projectSaleId as any,
        buyerId: user.id
      });

      toast.success("Project sale accepted successfully! Awaiting final confirmation.");

      // Refresh the data by refetching
      // The data will update automatically due to the useQuery hooks
    } catch (error: any) {
      console.error("Error accepting project sale:", error);
      toast.error(error.message || "Failed to accept project sale");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-12 sm:py-16 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t("projectSale.title") || "Project Sales"}
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t("projectSale.title") || "Project Sales"} {t("public.projects.title") || "Projects"}
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed mb-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t("projectSale.description") || "Browse projects available for full ownership purchase"}
              </p>
              <p className="text-sm sm:text-base text-primary-foreground/80 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <strong>{t("public.hero.slide2.description") || "Invest today and change your financial situation within 6 months"}</strong><br className="sm:hidden" />
                {t("public.hero.slide2.leftSubtitle") || "Direct investment in intellectual wealth through new financial mechanisms"}
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={t("common.search") || "ابحث عن مشروع..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 w-full"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full">
                  <Filter className="w-4 h-4 ml-2" />
                  <SelectValue placeholder={t("common.category") || "القطاع"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat, index) => (
                    <SelectItem key={index} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.length > 0 ? (
                filteredProjects.map((item) => {
                  const project = item;
                  const sale = item.sale;

                  return (
                    <div key={project._id} className="block">
                      <div
                        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-elevated transition-all duration-300 group"
                      >
                        <div className="p-4 sm:p-6 pb-4">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {project.category}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {t("status.funded") || "ممول بالكامل"}
                            </Badge>
                          </div>
                          <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {project.description}
                          </p>
                        </div>

                        <div className="p-4 sm:p-6 pt-4 bg-muted/30 border-t border-border">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                            <div className="flex items-center gap-2 text-sm">
                              <Target className="w-4 h-4 text-primary" />
                              <span className="text-muted-foreground">{t("projectSale.amountLabel") || "مبلغ البيع"}:</span>
                              <span className="font-semibold">{sale?.saleAmount ? <CurrencyDisplay amount={sale.saleAmount} /> : 'TBD'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <TrendingUp className="w-4 h-4 text-secondary" />
                              <span className="font-semibold text-secondary">{project.expectedROI || 0}% ROI</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-muted-foreground">{t("public.projects.fundingProgress") || "تقدم التمويل"}</span>
                            <span className="font-semibold">{Math.round((project.currentFunding / project.fundingGoal) * 100)}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-hero-gradient rounded-full transition-all duration-500"
                              style={{ width: `${Math.round((project.currentFunding / project.fundingGoal) * 100)}%` }}
                            />
                          </div>
                        </div>

                        <div className="p-4 sm:p-6 pt-4">
                          <Button
                            className="w-full bg-hero-gradient hover:opacity-90 text-primary-foreground"
                            onClick={() => {
                              // Navigate to project sale detail using the project ID
                              // The ProjectSaleDetail component will look up the sale by project ID if needed
                              navigate(`/project-sales/${project._id}`);
                            }}
                          >
                            {t("common.view") || "عرض التفاصيل"}
                          </Button>
                          <Button
                            className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-primary-foreground"
                            onClick={() => handleAcceptSale(sale._id)}
                          >
                            {t("projectSale.submitRequest") || "شراء المشروع"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-16 col-span-full">
                  <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t("common.noData") || "لا توجد بيانات"}</h3>
                  <p className="text-muted-foreground text-lg">
                    {t("projectSale.noProjectsAvailable") || "لا توجد مشاريع متاحة للبيع"}
                  </p>
                </div>
              )}
            </div>

            {filteredProjects.length === 0 && availableProjects.length > 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">
                  {t("common.noData") || "لا توجد مشاريع تطابق البحث"}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectSales;